import { useState } from 'react';
import { api } from '../api';

const fmt = v => '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 });

export default function TopCustomers({ data, start, end }) {
  const [expanded,  setExpanded]  = useState(null);
  const [custData,  setCustData]  = useState({});
  const [loading,   setLoading]   = useState(null);

  if (!data || data.length === 0) return (
    <div className="empty-state">No customer data for this period</div>
  );

  const toggle = async (customerId) => {
    if (expanded === customerId) { setExpanded(null); return; }
    setExpanded(customerId);
    if (!custData[customerId]) {
      setLoading(customerId);
      try {
        const res = await api.customerProducts(start, end, customerId);
        setCustData(prev => ({ ...prev, [customerId]: res }));
      } catch {
        setCustData(prev => ({ ...prev, [customerId]: null }));
      } finally {
        setLoading(null);
      }
    }
  };

  return (
    <div className="card">
      <div className="card-title">Top customers by revenue</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {data.map((c, i) => {
          const isOpen    = expanded  === c.customer_id;
          const isLoading = loading   === c.customer_id;
          const info      = custData[c.customer_id];
          const maxPRev   = info?.products?.[0]?.revenue || 1;

          return (
            <div key={c.customer_id}>

              {/* ── Customer row ─────────────────── */}
              <div
                onClick={() => toggle(c.customer_id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px 110px 1fr 120px 100px 60px 110px',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: isOpen ? 'var(--surface-hover)' : 'transparent',
                  border: `1px solid ${isOpen ? 'var(--accent-border)' : 'transparent'}`,
                  transition: 'all .15s',
                }}
              >
                {/* Rank */}
                <div style={{ fontSize: '11px', color: 'var(--light)', textAlign: 'center' }}>
                  {i + 1}
                </div>

                {/* Customer ID */}
                <span className="badge" style={{ background: 'var(--blue-light)', color: 'var(--blue-dark)', fontSize: '11px' }}>
                  {c.customer_id}
                </span>

                {/* Name + arrow */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                  <span style={{
                    fontSize: '11px',
                    color: isOpen ? 'var(--green)' : 'var(--muted)',
                    transition: 'transform .2s',
                    display: 'inline-block',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    flexShrink: 0,
                  }}>▶</span>
                  <span style={{
                    fontWeight: 500, fontSize: '13px',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {c.name}
                  </span>
                </div>

                {/* City */}
                <div style={{ fontSize: '12px', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.city}
                </div>

                {/* State */}
                <div style={{ fontSize: '12px', color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.state}
                </div>

                {/* Bills */}
                <div style={{ textAlign: 'center', fontSize: '12px' }}>
                  {c.total_bills}
                </div>

                {/* Revenue + product badge */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: 'var(--green)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                    {fmt(c.revenue)}
                  </div>
                  {info && (
                    <div style={{ fontSize: '10px', color: 'var(--teal)', marginTop: '2px' }}>
                      {info.total_products} products
                    </div>
                  )}
                </div>
              </div>

              {/* ── Expanded panel ───────────────── */}
              {isOpen && (
                <div style={{
                  margin: '0 0 4px 40px',
                  background: 'var(--surface-tint)',
                  border: '1px solid var(--accent-border)',
                  borderTop: '2px solid var(--green)',
                  borderRadius: '0 0 10px 10px',
                  padding: '14px 16px',
                  animation: 'slideDown .2s ease',
                }}>

                  {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '16px', color: 'var(--muted)', fontSize: '13px' }}>
                      <span className="spinner" /> Loading purchased products…
                    </div>

                  ) : !info ? (
                    <div style={{ color: 'var(--muted)', fontSize: '13px' }}>Failed to load</div>

                  ) : (
                    <>
                      {/* Customer summary strip */}
                      <div style={{
                        display: 'flex', gap: '16px', flexWrap: 'wrap',
                        marginBottom: '14px', paddingBottom: '12px',
                        borderBottom: '1px solid var(--border)',
                      }}>
                        {[
                          { label: 'Total Spent',  value: fmt(info.total_revenue), color: 'var(--green)' },
                          { label: 'Total Bills',  value: info.total_bills,         color: 'var(--blue)' },
                          { label: 'Products Bought', value: info.total_products,   color: 'var(--violet)' },
                        ].map(s => (
                          <div key={s.label} style={{ textAlign: 'center', minWidth: '80px' }}>
                            <div style={{ fontSize: '15px', fontWeight: 700, color: s.color }}>{s.value}</div>
                            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>{s.label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Section title */}
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--green-dark)', marginBottom: '10px' }}>
                        🛒 Products purchased by {c.name}
                      </div>

                      {/* Product list */}
                      {info.products.length === 0 ? (
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>No products found</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {info.products.map((p, pi) => (
                            <div key={p.product_name} style={{
                              display: 'grid',
                              gridTemplateColumns: '24px 1fr 100px 55px 65px 80px',
                              alignItems: 'center',
                              gap: '10px',
                              fontSize: '12px',
                              padding: '6px 8px',
                              borderRadius: '6px',
                              background: pi === 0 ? 'var(--surface-hover)' : pi % 2 === 0 ? 'var(--row-zebra)' : 'transparent',
                            }}>

                              {/* Rank circle */}
                              <span style={{
                                width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
                                background: pi < 3 ? ['var(--green)','var(--blue)','var(--amber)'][pi] : 'var(--border)',
                                color: pi < 3 ? '#fff' : 'var(--muted)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '9px', fontWeight: 700,
                              }}>
                                {pi + 1}
                              </span>

                              {/* Product name + mini bar */}
                              <div>
                                <div style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '3px' }}>
                                  {p.product_name}
                                </div>
                                <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
                                  <div style={{
                                    height: '100%', borderRadius: '2px',
                                    background: pi === 0 ? 'var(--green)' : pi === 1 ? 'var(--blue)' : 'var(--light)',
                                    width: `${(p.revenue / maxPRev) * 100}%`,
                                    transition: 'width .5s ease',
                                  }} />
                                </div>
                              </div>

                              {/* Revenue */}
                              <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap' }}>
                                {fmt(p.revenue)}
                              </div>

                              {/* Qty */}
                              <div style={{ textAlign: 'center', color: 'var(--muted)' }}>
                                {p.qty} qty
                              </div>

                              {/* Orders badge */}
                              <div style={{ textAlign: 'center' }}>
                                <span style={{
                                  background: 'var(--green-light)', color: 'var(--green-dark)',
                                  padding: '1px 6px', borderRadius: '999px', fontSize: '10px',
                                }}>
                                  {p.orders} orders
                                </span>
                              </div>

                              {/* Last purchase date */}
                              <div style={{ fontSize: '10px', color: 'var(--muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {p.last_date}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}